import React, { useRef, useCallback, useState, useEffect } from 'react';
import styles from './sidebar.module.css';
import ReactTooltip from 'react-tooltip';
import { Footer } from './footer';
import ContextInputs from 'common/context-input';
import SearchInput from 'common/search-input';
import content_styles from './content.module.css';
import FilterBy from 'common/filters/filter-by';
import { useDispatch, useSelector } from 'react-redux';
import { selectConnection, selectFavorites } from 'store/core/selectors';
import DropDown from 'common/drop-down';
import { toggleFavorite } from 'store/core/actions';
import { setSearch, setGraphItem, setIsLoadingGraphItems, updateSearchContextDisplay } from 'store/graph/actions';
import { getDataDate } from 'store/core/connection';
import { getItems } from 'utils/graph-helper';

export const Sidebar = ({ isCollapsed, setIsCollapsed, rootRef }) => {
  const sidebarRef = useRef(null);
  const conn = useSelector(selectConnection);
  const [dataDate, setDate] = useState(undefined);

  useEffect(() => {
    if (conn) {
      setTimeout(() => {
        const date = getDataDate();
        setDate(new Date(date).toLocaleDateString());
      }, 2000);
    }
  }, [conn]);

  const arrowHoverValue = isCollapsed ? 'Open sidebar' : 'Close sidebar';
  const ArrowButton = useCallback(() => {
    return (
      <div
        className={styles.pin_icon}
        onClick={() => {
          setIsCollapsed(!isCollapsed);
        }}
        data-tip
        data-for="arrow-button-id"
      >
        <div
          className={`${styles.pin_icon1} ${
            isCollapsed ? styles.collapsed_p1 : styles.p1
          }`}
        ></div>
        <div
          className={`${styles.pin_icon2} ${
            isCollapsed ? styles.collapsed_p2 : styles.p2
          }`}
        ></div>
        <ReactTooltip id="arrow-button-id" place="top" effect="solid">
          {arrowHoverValue}
        </ReactTooltip>
      </div>
    );
  }, [isCollapsed]);

  const innerComponentRef = useRef(null);
  const outerRef = useRef(null);
  const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';

  return (
    <div
      ref={sidebarRef}
      className={`${styles.root} ${!isCollapsed && styles.root__pinned} ${
        isCollapsed && styles.collapsed_root
      }  `}
    >
      <div
        className={`${
          isCollapsed
            ? styles.collapsed_body
            : styles.not_collapsed_body__pinned
        }`}
      >
        <ArrowButton />
        {!isCollapsed && (
          <div className={styles.sidebar_menu}>
            <div className={styles.not_collapsed_body_header}>
              <span className={styles.not_collapsed_body_header_text}>
                Foundation Tool
              </span>
              {isDev && <span className={styles.dev_text}>Dev</span>}
            </div>
            <div className={styles.content} ref={outerRef}>
              <div
                style={{ height: '100%', width: '100%' }}
                ref={innerComponentRef}
              >
                <NavigationContent />
              </div>
            </div>
            <Footer
              isCollapsed={isCollapsed}
              dataDate={dataDate}
              rootRef={rootRef}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export const NavigationContent = () => {
  const favorites = useSelector(selectFavorites);
  const connection = useSelector(selectConnection);
  const dispatch = useDispatch();
  const [favOpen, setFavOpen] = useState(true);

  const onLoadFavorite = (fav) => {
    if (!fav || !fav.context) return;
    const ctx = fav.context;
    // Check if favorite has a cached blob URL (both nodes and regions)
    const blobUrl = fav.blobUrl || ctx.blobUrl;
    const isRegion = !!ctx.geoType; // Check for geoType to identify geographic items
    
    if (blobUrl) {
      // Load ONLY from cached blob - no backend fallback
      const displayValue = ctx.displayValue || ctx.value || ctx.metroRegion;
      // For regions, use composite ID (type|value) to match search-input behavior and unique identification
      const contextId = isRegion ? `${ctx.geoType}|${ctx.geoValue}` : ctx.id;
      const uiContext = { ...ctx, id: contextId, displayValue, value: displayValue, blobUrl };
      dispatch(updateSearchContextDisplay(uiContext));
      dispatch(setIsLoadingGraphItems(true));
      
      fetch(blobUrl)
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch cached graph data');
          }
          return res.json();
        })
        .then((data) => {
          const normalizedData = {
            vertices: data.Vertices || data.vertices,
            edges: data.Edges || data.edges,
          };
          const processed = getItems(normalizedData, isRegion ? null : ctx.id);
          if (processed) {
            dispatch(
              setGraphItem(
                uiContext,
                processed.nodes,
                processed.links,
                processed.nodeCount,
              ),
            );
          }
        })
        .catch(() => {
          dispatch(setIsLoadingGraphItems(false));
        })
        .finally(() => {
          dispatch(setIsLoadingGraphItems(false));
        });
    } else {
      // No blob URL - use saga for backend query
      dispatch(setSearch(ctx));
    }
  };
  const onRemoveFavorite = (fav) => {
    dispatch(toggleFavorite(fav));
  };
  return (
    <>
      <div className={content_styles.search_wrapper}>
        <ContextInputs />
        <SearchInput />
      </div>
      {favorites && favorites.length > 0 && (
        <div className={styles.favorites_section}>
          <DropDown
            filterText="Favorites"
            onClick={() => setFavOpen(!favOpen)}
            open={favOpen}
          />
          {favOpen && (
            <div className={styles.favorites_list}>
              {favorites.map((fav, idx) => (
                <div key={`${fav.nodeId || fav.context?.metroRegion || fav.displayName}-${idx}`} className={styles.favorite_item}>
                  <span onClick={() => onLoadFavorite(fav)}>{fav.displayName}</span>
                  <span
                    className={styles.favorite_remove}
                    onClick={() => onRemoveFavorite(fav)}
                  >
                    ×
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div>
        <FilterBy />
      </div>
    </>
  );
};
